import { navigateToUrl, registerApplication, SingleSpaCustomEventDetail, start } from "single-spa";
import { importRemote } from "./microfrontends/import-remote";
import { loadSettingsV2 } from "./microfrontends/load-settings";
import { loadScript } from "./microfrontends/load-script";
import { getLTIAttemptsToken } from "@edgenuity/cwng-educator-api";

let isAuthenticated = false;
let isPendoInitialized = false;

loadConfigurations().then(() => {
  singleSpaStart();
});

async function singleSpaStart() {
  const defaultUrl = "/catalog";
  const {
    auth$,
    COURSE_UI_URL,
    SECTION_UI_URL,
    ENROLLMENT_UI_URL,
    GRADING_UI_URL,
    MARKETING_UI_URL,
    CATALOG_UI_URL,
    LICENSING_UI_URL,
    ATTENDANCE_LOG_UI_URL,
    AUTHORING_UI_URL,
    DCO_UI_URL,
    SESSION_LOG_UI_URL,
    EDUCATOR_LAUNCHPAD_UI_URL,
    PROGRESS_UI_URL,
    IMPERSONATION_BANNER_UI_URL,
    getAuthToken,
    getAuthTokenAsync,
    getAuthenticatedUser,
    getLmsBaseUrl,
    decodeToken,
    logout,
    getRefreshToken,
    refreshAuthTokens,
    isValidToken,
    getLTIAttemptsToken,
  } = await import("@edgenuity/cwng-educator-api");

  registerApplication(
    "header",
    // @ts-ignore
    () => import("@edgenuity/cwng-educator-header/single-spa"),
    (location) => !location.pathname.startsWith("/login"),
    { getAuthToken, decodeToken: decodeToken },
  );

  registerApplication(
    "login",
    // @ts-ignore
    () => import("@edgenuity/cwng-educator-login/single-spa"),
    (location) => location.pathname.startsWith("/login"),
  );

  registerApplication(
    "course",
    () => importRemote(COURSE_UI_URL, "course", "./single-spa"),
    (location) => location.pathname.startsWith("/course"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "section",
    () => importRemote(SECTION_UI_URL, "section", "./single-spa"),
    (location) => location.pathname.startsWith("/section") || location.pathname.startsWith("/sections"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "enrollment",
    () => importRemote(ENROLLMENT_UI_URL, "enrollment", "./single-spa"),
    (location) => location.pathname.startsWith("/enrollment") || location.pathname.startsWith("/enrollments"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "grading",
    () => importRemote(GRADING_UI_URL, "grading", "./single-spa"),
    (location) => location.pathname.startsWith("/grade-book"),
    { getAuthToken, getAuthTokenAsync, getLTIAttemptsToken },
  );

  registerApplication(
    "impersonation-banner",
    // @ts-ignore
    () => importRemote(`${IMPERSONATION_BANNER_UI_URL}`, "impersonationBanner", "./single-spa"),
    (location) => location.pathname.startsWith("/") && location.pathname !== "/login",
    {
      getAuthToken,
      getAuthTokenAsync,
      getLmsBaseUrl: getLmsBaseUrl,
      decodeToken: decodeToken,
      logout: logout,
    },
  );

  registerApplication(
    "educator_launchpad",
    () => importRemote(EDUCATOR_LAUNCHPAD_UI_URL, "educator_launchpad", "./single-spa"),
    (location) => location.pathname.startsWith("/educator-launchpad"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "marketing",
    () => importRemote(MARKETING_UI_URL, "marketing", "./single-spa"),
    (location) => /(^\/catalog\/?$)/.test(location.pathname),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "catalog",
    () => importRemote(CATALOG_UI_URL, "catalog", "./single-spa"),
    (location) => location.pathname.startsWith("/catalog"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "licensing",
    () => importRemote(LICENSING_UI_URL, "licensing", "./single-spa"),
    (location) => location.pathname.startsWith("/licensing"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "attendance_log",
    () => importRemote(ATTENDANCE_LOG_UI_URL, "attendance_log", "./single-spa"),
    (location) => location.pathname.startsWith("/attendance-log"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "authoring",
    () => importRemote(AUTHORING_UI_URL, "authoring", "./single-spa"),
    (location) => location.pathname.startsWith("/authoring"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "session_log",
    () => importRemote(SESSION_LOG_UI_URL, "session_log", "./single-spa"),
    (location) => location.pathname.startsWith("/session-log"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "dco",
    () => importRemote(DCO_UI_URL, "dco", "./single-spa"),
    (location) => location.pathname.startsWith("/default-course-options"),
    { getAuthToken, getAuthTokenAsync },
  );

  registerApplication(
    "progress_report",
    () => importRemote(PROGRESS_UI_URL, "progress_report", "./single-spa"),
    (location) => location.pathname.startsWith("/progress-report"),
    { getAuthToken, getAuthTokenAsync },
  );

  const token = getAuthToken();
  if (!isValidToken(token)) {
    const refreshToken = getRefreshToken();

    if (refreshToken) {
      await refreshAuthTokens();
    }
  }

  auth$.next({
    error: null,
    isLoading: false,
    isAuthenticated: isValidToken(token),
  });

  auth$.subscribe((value) => {
    if (value.isLoading) {
      return;
    }

    isAuthenticated = value.isAuthenticated;
    if (!isAuthenticated && window.location.pathname !== "/login") {
      navigateToUrl("/login");
    }

    if (isAuthenticated && window.location.pathname === "/login") {
      navigateToUrl(defaultUrl);
    }

    if (isAuthenticated && !isPendoInitialized) {
      try {
        const userData = getAuthenticatedUser();
        // @ts-ignore
        window.pendo.initialize({
          visitor: {
            id: userData.username,
          },
        });
        isPendoInitialized = true;
      } catch (error) {
        console.error("PendoError", error);
      }
    }
  });

  window.addEventListener("single-spa:before-routing-event", (e: CustomEvent<SingleSpaCustomEventDetail>) => {
    if (!isAuthenticated) {
      navigateToUrl("/login");
      return;
    }

    if (window.location.pathname === "/") {
      navigateToUrl(defaultUrl);
    }
  });

  start();
}

async function loadConfigurations() {
  const {
    CATALOG_UI_URL,
    ATTENDANCE_LOG_UI_URL,
    AUTHORING_UI_URL,
    DCO_UI_URL,
    BULKFILE_UI_URL,
    IMPERSONATION_BANNER_UI_URL,
    COURSE_UI_URL,
    ENROLLMENT_UI_URL,
    GRADING_UI_URL,
    LICENSING_UI_URL,
    SECTION_UI_URL,
    EDUCATOR_LAUNCHPAD_UI_URL,
    PROGRESS_UI_URL,
    ENV,
  } = await import("@edgenuity/cwng-educator-api");

  function getBaseUrl(url: string) {
    try {
      const urlObject = new URL(url);
      const baseURL = urlObject.origin + urlObject.pathname.substring(0, urlObject.pathname.lastIndexOf("/"));
      if (baseURL.includes("localhost")) {
        return "http://localhost:3010"
      }
      return baseURL;
    } catch (e) {
      console.error("Invalid URL:", url, e);
      return null;
    }
  }

  if (ENV === "local") {
    return Promise.all([]);
  } else {
    // @ts-ignore
    window["EDUCATOR_ENV"] = Object.freeze({
      "env": window["APP_ENV"].env
    });

    return Promise.all([
      CATALOG_UI_URL.includes("courseware") ?
        loadSettingsV2("CATALOG_ENV", `${getBaseUrl(CATALOG_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(CATALOG_UI_URL)}/env/catalog.env.js`),
      COURSE_UI_URL.includes("courseware") ?
        loadSettingsV2("COURSE_ENV", `${getBaseUrl(COURSE_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(COURSE_UI_URL)}/env/course.env.js`),
      SECTION_UI_URL.includes("courseware") ?
        loadSettingsV2("SECTION_ENV", `${getBaseUrl(SECTION_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(SECTION_UI_URL)}/env/section.env.js`),
      ENROLLMENT_UI_URL.includes("courseware") ?
        loadSettingsV2("ENROLLMENT_ENV", `${getBaseUrl(ENROLLMENT_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(ENROLLMENT_UI_URL)}/env/enrollment.env.js`),
      GRADING_UI_URL.includes("courseware") ?
        loadSettingsV2("GRADING_ENV", `${getBaseUrl(GRADING_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(GRADING_UI_URL)}/env/grading.env.js`),
      LICENSING_UI_URL.includes("courseware") ?
        loadSettingsV2("LICENSING_ENV", `${getBaseUrl(LICENSING_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(LICENSING_UI_URL)}/env/licensing.env.js`),
      ATTENDANCE_LOG_UI_URL.includes("courseware") ?
        loadSettingsV2("ATTENDANCE_LOG_ENV", `${getBaseUrl(ATTENDANCE_LOG_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(ATTENDANCE_LOG_UI_URL)}/env/attendance-log.env.js`),
      AUTHORING_UI_URL.includes("courseware") ?
        loadSettingsV2("AUTHORING_ENV", `${getBaseUrl(AUTHORING_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(AUTHORING_UI_URL)}/env/authoring.env.js`),
      DCO_UI_URL.includes("courseware") ?
        loadSettingsV2("DCO_ENV", `${getBaseUrl(DCO_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(DCO_UI_URL)}/env/dco.env.js`),
      BULKFILE_UI_URL.includes("courseware") ?
        loadSettingsV2("BULKFILE_ENV", `${getBaseUrl(BULKFILE_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(BULKFILE_UI_URL)}/env/bulkfile.env.js`),
      IMPERSONATION_BANNER_UI_URL.includes("courseware") ?
        loadSettingsV2("EDUCATOR_APPLICATION_BANNER_ENV", `${getBaseUrl(IMPERSONATION_BANNER_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(IMPERSONATION_BANNER_UI_URL)}/env/educator-application-banner.env.js`),
      EDUCATOR_LAUNCHPAD_UI_URL.includes("courseware") ?
        loadSettingsV2("EDUCATOR_LAUNCHPAD_ENV", `${getBaseUrl(EDUCATOR_LAUNCHPAD_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(EDUCATOR_LAUNCHPAD_UI_URL)}/env/educator-launchpad.env.js`),
      PROGRESS_UI_URL.includes("courseware") ?
        loadSettingsV2("PROGRESS_REPORT_ENV", `${getBaseUrl(PROGRESS_UI_URL)}/settings/env.json`) :
        loadScript(`${getBaseUrl(PROGRESS_UI_URL)}/env/progress-report.env.js`),
    ]);
  }
}
